import React, { useEffect, useState } from "react";
import UserSelectControl from "./UserSelectControl";
import { NewTeam } from "./types";
import {
  CustomizableSelect,
  FormGroup,
  Toggle
} from "@velaro/velaro-component-lib";
import TeamFieldRow from "./TeamFieldRow";

interface Props {
  team: NewTeam;
  onChange(team: Partial<NewTeam>): void;
}

export default function TeamForm({ team, onChange }: Props) {
  const [autoResolve, setAutoResolve] = useState<boolean>(team.autoResolve);
  const [botAutoReassignment, setBotAutoReassignment] = useState<boolean>(
    team.botAutoReassignment
  );
  const [transferToAgentButtonEnabled, setTransferToAgentButtonEnabled] =
    useState<boolean>(team.transferToAgentButtonEnabled);

  //# Dropdown options
  const routingActions: Array<NewTeam["routingAction"]> = [
    "AutoRoute",
    "Queue"
  ];
  const unassignActions: Array<NewTeam["unassignAction"]> = [
    "Reroute",
    "Nothing"
  ];
  const routingTypes: Array<NewTeam["routingType"]> = [
    "roundRobin",
    "leastBusy"
  ];
  const inactiveAgentActions: Array<NewTeam["inactiveAgentAction"]> = [
    "UnassignAgent",
    "MakeUnavailable"
  ];

  // Optional: labels for user-friendly display
  const routingActionLabels: Record<NewTeam["routingAction"], string> = {
    AutoRoute: "route to agent",
    Queue: "send to queue"
  };

  const unassignLabels: Record<NewTeam["unassignAction"], string> = {
    Reroute: "route to a new agent",
    Nothing: "do nothing"
  };

  const routingTypeLabels: Record<NewTeam["routingType"], string> = {
    roundRobin: "Round Robin",
    leastBusy: "Round Robin - Least Busy"
  };

  const inactiveAgentLabels: Record<NewTeam["inactiveAgentAction"], string> = {
    UnassignAgent: "unassign the agent",
    MakeUnavailable: "set the conversation as missed"
  };

  // State for selected value, initialized from props.team
  const [selectRoutingAction, setRoutingAction] = useState<
    NewTeam["routingAction"]
  >(team.routingAction);
  const [selectedUnassign, setSelectedUnassign] = useState<
    NewTeam["unassignAction"]
  >(team.unassignAction);
  const [selectedRouting, setSelectedRouting] = useState<
    NewTeam["routingType"]
  >(team.routingType);
  const [selectedInactive, setSelectedInactive] = useState<
    NewTeam["inactiveAgentAction"]
  >(team.inactiveAgentAction);

  // Keep local state in sync if props.team changes
  useEffect(() => {
    setRoutingAction(team.routingAction);
  }, [team.routingAction]);

  useEffect(
    () => setSelectedUnassign(team.unassignAction),
    [team.unassignAction]
  );
  useEffect(() => setSelectedRouting(team.routingType), [team.routingType]);
  useEffect(
    () => setSelectedInactive(team.inactiveAgentAction),
    [team.inactiveAgentAction]
  );

  return (
    <>
      <TeamFieldRow
        title="Team Name"
        subtitle=""
        input={
          <input
            type="text"
            required
            value={team.name}
            onChange={(e) => onChange({ name: e.currentTarget.value })}
            className="w-full p-2 border-[1px] rounded-md text-sm text-slate-500"
          />
        }
      />
      <TeamFieldRow
        title="Users"
        subtitle=""
        input={
          <UserSelectControl
            selectedUserIds={team.userIds}
            onChange={(userIds) => onChange({ userIds })}
          />
        }
      />
      <TeamFieldRow
        title="On incoming chat"
        subtitle=""
        input={
          <>
            <div className="form-group">
              <select
                id="routingActionSelect"
                className="w-full p-2 border-[1px] rounded-md text-sm text-slate-500"
                value={selectRoutingAction}
                onChange={(e) => {
                  const value = e.target.value as NewTeam["routingAction"];
                  setRoutingAction(value);
                  onChange({
                    ...team,
                    routingAction: e.target.value as NewTeam["routingAction"]
                  });
                }}
              >
                {routingActions.map((action) => (
                  <option key={action} value={action}>
                    {routingActionLabels[action]}
                  </option>
                ))}
              </select>
            </div>
          </>
        }
      />
      {team.routingAction === "AutoRoute" && (
        <>
          <TeamFieldRow
            title="When chat is unassigned"
            subtitle=""
            input={
              <>
                <div className="form-group">
                  <select
                    className="w-full p-2 border-[1px] rounded-md text-sm text-slate-500"
                    value={selectedUnassign}
                    onChange={(e) => {
                      const value = e.target.value as NewTeam["unassignAction"];
                      setSelectedUnassign(value);
                      onChange({ ...team, unassignAction: value });
                    }}
                  >
                    {unassignActions.map((action) => (
                      <option key={action} value={action}>
                        {unassignLabels[action]}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            }
          />
          <TeamFieldRow
            title="Routing Type"
            subtitle=""
            input={
              <>
                <div className="form-group">
                  <select
                    className="w-full p-2 border-[1px] rounded-md text-sm text-slate-500"
                    value={selectedRouting}
                    onChange={(e) => {
                      const value = e.target.value as NewTeam["routingType"];
                      setSelectedRouting(value);
                      onChange({ ...team, routingType: value });
                    }}
                  >
                    {routingTypes.map((type) => (
                      <option key={type} value={type}>
                        {routingTypeLabels[type]}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            }
          />
        </>
      )}
      <TeamFieldRow
        title="When the agent is inactive"
        subtitle=""
        input={
          <>
            <div className="form-group">
              <select
                className="w-full p-2 border-[1px] rounded-md text-sm text-slate-500"
                value={selectedInactive}
                onChange={(e) => {
                  const value = e.target
                    .value as NewTeam["inactiveAgentAction"];
                  setSelectedInactive(value);
                  onChange({ ...team, inactiveAgentAction: value });
                }}
              >
                {inactiveAgentActions.map((action) => (
                  <option key={action} value={action}>
                    {inactiveAgentLabels[action]}
                  </option>
                ))}
              </select>
            </div>
          </>
        }
      />
      {team.inactiveAgentAction === "UnassignAgent" && (
        <div>
          <TeamFieldRow
            title="Inactive Warning Time"
            subtitle=""
            input={
              <>
                <div className="">
                  <input
                    id="unassignWarningTime"
                    type="text"
                    className="mx-2 input"
                    required
                    value={team.unassignWarningTime}
                    onChange={(e) =>
                      onChange({
                        unassignWarningTime: Number(e.currentTarget.value)
                      })
                    }
                  />
                  Minutes
                </div>
              </>
            }
          />
          <TeamFieldRow
            title="Inactive Unassign Time"
            subtitle=""
            input={
              <>
                <div className="">
                  <input
                    id="unassignTime"
                    type="text"
                    className="mx-2 input"
                    required
                    value={team.unassignTime}
                    onChange={(e) =>
                      onChange({ unassignTime: Number(e.currentTarget.value) })
                    }
                  />
                  Minutes
                </div>
              </>
            }
          />
        </div>
      )}
      <div className="w-full mb-6">
        <div className="flex items-center justify-between gap-3 mb-2">
          <label className="block text-sm font-normal text-black">
            <b>Automatically resolve conversations</b>
          </label>
          <Toggle
            toggled={autoResolve}
            changeToggle={() => {
              onChange({ ...team, autoResolve: !autoResolve });
              setAutoResolve(!autoResolve);
            }}
          />
        </div>
        <div className="text-sm text-slate-600 mb-2"></div>
      </div>
      {team.autoResolve && (
        <div>
          <TeamFieldRow
            title="Auto Resolve Time"
            subtitle=""
            input={
              <>
                <div className="">
                  <input
                    id="autoResolveTime"
                    type="text"
                    className="mx-2 input"
                    required
                    value={team.autoResolveTime}
                    onChange={(e) =>
                      onChange({
                        autoResolveTime: Number(e.currentTarget.value)
                      })
                    }
                  />
                  Minutes
                </div>
              </>
            }
          />
        </div>
      )}
      <div className="w-full mb-6">
        <div className="flex items-center justify-between gap-3 mb-2">
          <label className="block text-sm font-normal text-black">
            <b>Auto Transfer from Bot</b>
          </label>
          <Toggle
            toggled={botAutoReassignment}
            changeToggle={() => {
              onChange({ ...team, botAutoReassignment: !botAutoReassignment });
              setBotAutoReassignment(!botAutoReassignment);
            }}
          />
        </div>
        <div className="text-sm text-slate-600 mb-2"></div>
      </div>
      {team.botAutoReassignment && (
        <div>
          <TeamFieldRow
            title="Auto Transfer from Bot Time"
            subtitle=""
            input={
              <>
                <div className="">
                  <input
                    id="autoTransferFromBotTime"
                    type="text"
                    className="mx-2 input"
                    required
                    value={team.botAutoReassignmentTime}
                    onChange={(e) =>
                      onChange({
                        botAutoReassignmentTime: Number(e.currentTarget.value)
                      })
                    }
                  />
                  Minutes
                </div>
              </>
            }
          />
        </div>
      )}
      <div className="w-full mb-6">
        <div className="flex items-center justify-between gap-3 mb-2">
          <label className="block text-sm font-normal text-black">
            <b>Enable Transfer to Agent Button for AI Conversations</b>
          </label>
          <Toggle
            toggled={transferToAgentButtonEnabled}
            changeToggle={() => {
              onChange({
                ...team,
                transferToAgentButtonEnabled: !transferToAgentButtonEnabled
              });
              setTransferToAgentButtonEnabled(!transferToAgentButtonEnabled);
            }}
          />
        </div>
        <div className="text-sm text-slate-600 mb-2"></div>
      </div>
    </>
  );
}
